/**
 * PartyKit 实时客户端（架构 §2 src/socket/client.ts / §7.3 / T14）
 *
 *  - 单例 SocketManager：按会话（房间）维护 PartySocket 连接（房间 = conversationId）。
 *  - 连接地址：dev 经 Vite 代理 -> ws://<host>/party/parties/main/<roomId>?token=<JWT>
 *             （Vite 把 /party 重写剥掉，落到 PartyKit 实际路径 /parties/main/<roomId>）
 *             prod -> wss://<PARTYKIT_HOST>/parties/main/<roomId>?token=<JWT>
 *  - token 用登录 JWT；token 变化时重新建连（重连）。
 *  - 事件（服务端广播 JSON.stringify({ type, payload })）驱动 chat.store：
 *      message:new      -> 追加消息（含自己/他人文本、AI 文本、文件卡片）
 *      message:history  -> 历史（initial 替换 / older 前插）
 *      connect_ok       -> 防御性（服务端当前 onOpen 即视为就绪）
 *      ai:typing        -> 知会思考中指示
 *      error            -> 错误码横幅（3001/3002/3003）或鉴权失效跳登录
 *
 * 发消息只用 PartyKit 单一路径（emit message:send），避免 AI 双触发。
 */
import { PartySocket } from 'partysocket';
import { useAuthStore } from '../store/auth.store';
import { useChatStore } from '../store/chat.store';
import { useArtifactStore } from '../store/artifact.store';
import type { AttachmentInput, Message, Artifact, AiNotice } from '../types/models';
import { ErrorCode } from '../types/models';

// 解析 WS 主机：优先 VITE_PARTYKIT_HOST；生产未注入时回退到 VITE_API_BASE 的 host
// （去掉 /api/v1 后缀与协议前缀），从而直连 Worker（zhihui-server.workers.dev）的 WS。
// dev 环境两者均缺失时 PARTYKIT_HOST 为空 -> IS_DEV=true，走 Vite 代理 /party 前缀。
const API_BASE_HOST = (import.meta.env.VITE_API_BASE || '')
  .replace(/\/api\/v1\/?$/i, '')
  .replace(/^https?:\/\//, '')
  .replace(/\/+$/, '');
const PARTYKIT_HOST =
  (import.meta.env.VITE_PARTYKIT_HOST || API_BASE_HOST || '').replace(/^https?:\/\//, '') || '';
const IS_DEV = !PARTYKIT_HOST;

export const FEEDBACK_EVENT = 'zhihui:feedback';
export function emitFeedback(severity: 'info' | 'warning' | 'error', text: string) {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(FEEDBACK_EVENT, { detail: { severity, text } }));
  }
}

export interface SendMessagePayload {
  conversationId: number;
  content: string;
  attachments?: AttachmentInput[];
  hasImage?: boolean;
  replyTo?: number | null;
}

interface PendingOutgoingMessage {
  content: string;
  attachments?: AttachmentInput[];
  replyTo?: number | null;
  timer: ReturnType<typeof setTimeout>;
}

class SocketManager {
  private sockets = new Map<number, PartySocket>();
  private historyMode = new Map<number, 'initial' | 'older'>();
  private pendingHistory = new Map<number, { mode: 'initial' | 'older'; cursor?: number; limit: number }>();
  private pendingMessages = new Map<number, PendingOutgoingMessage[]>();
  private statuses = new Map<number, 'connecting' | 'open' | 'closed'>();
  private degradedNotified = new Set<number>();

  private buildHost(): string {
    if (IS_DEV) {
      // 经 Vite 代理（同源），host 含端口
      return window.location.host;
    }
    return PARTYKIT_HOST;
  }

  private buildPrefix(): string {
    // 注意：partysocket 的 getPartyInfo 在拼 URL 的模板里，host 之后自带一个分隔斜杠
    // （`${protocol}://${host}/` + `${prefix}/...`），因此 prefix 不能带前导斜杠，
    // 否则线上 URL 会变成 `//parties`，与 Worker 路由 /parties/main/:room 不匹配，
    // 浏览器报 WebSocket connection failed（即问题 B 的根因）。
    // dev：经 Vite 代理（/party 由 vite.config.ts 重写剥离，落为 /parties）；prod：直连 /parties。
    return IS_DEV ? 'party/parties' : 'parties';
  }

  private makeSocket(conversationId: number): PartySocket {
    const token = useAuthStore.getState().token;
    const socket = new PartySocket({
      host: this.buildHost(),
      party: 'main',
      room: String(conversationId),
      prefix: this.buildPrefix(),
      query: { token: token ?? '' },
    });

    socket.addEventListener('open', () => {
      // 服务端 onConnect 校验 token；打开即视为连接就绪（无独立 connect_ok 事件）
      this.statuses.set(conversationId, 'open');
      this.degradedNotified.delete(conversationId);
      useChatStore.getState().setAiTyping(conversationId, false);
      this.flushPendingHistory(conversationId);
      this.flushPendingMessages(conversationId);
    });

    socket.addEventListener('message', (event: MessageEvent) => {
      this.handleEvent(conversationId, event.data);
    });

    socket.addEventListener('close', (event: CloseEvent) => {
      // BUG-017（#3b）：区分关闭码，识别 DO 鉴权失败（4001 invalid token / 4003 无权限）。
      // 此时继续自动重连只会产生重连告警风暴，应停止重连并触发登出。
      const code = (event as CloseEvent)?.code;
      if (code === 4001 || code === 4003) {
        // 鉴权失败：停止自动重连，触发登出，避免重连告警风暴。
        // partysocket 默认 _shouldReconnect=true；此处覆盖为 false 以阻断无限重连。
        (socket as unknown as { _shouldReconnect?: boolean })._shouldReconnect = false;
        useAuthStore.getState().logout();
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('zhihui:auth-expired'));
        }
        this.sockets.delete(conversationId);
        this.clearPendingMessages(conversationId, true);
        return;
      }
      // 其余关闭码（网络抖动等）：保留自动重连；清连接态 + AI typing 态
      this.statuses.set(conversationId, 'closed');
      useChatStore.getState().setAiTyping(conversationId, false);
    });

    socket.addEventListener('error', () => {
      // Disable reconnect storms when the realtime service is unavailable.
      const wasOpen = this.getStatus(conversationId) === 'open';
      this.statuses.set(conversationId, 'closed');
      (socket as unknown as { _shouldReconnect?: boolean })._shouldReconnect = false;
      this.clearPendingMessages(conversationId);
      if (!this.degradedNotified.has(conversationId)) {
        this.degradedNotified.add(conversationId);
        emitFeedback(wasOpen ? 'error' : 'warning', '\u5b9e\u65f6\u8fde\u63a5\u4e0d\u53ef\u7528\uff0c\u5df2\u5207\u6362\u4e3a\u666e\u901a\u53d1\u9001\u6a21\u5f0f');
      }
    });

    return socket;
  }

  private handleEvent(conversationId: number, raw: string) {
    let parsed: { type?: string; payload?: unknown };
    try {
      parsed = JSON.parse(raw);
    } catch {
      return;
    }
    const { type, payload } = parsed;
    const chat = useChatStore.getState();

    switch (type) {
      case 'connect_ok':
        // 防御性：服务端目前用 onOpen 视为就绪
        break;
      case 'heartbeat':
        // BUG-017（#3c）：DO 每 20s 单播心跳，仅用于保持连接活跃，前端无需处理
        break;
      case 'message:new': {
        const msg = payload as Message;
        if (msg && typeof msg.id === 'number') {
          chat.appendMessage(conversationId, msg);
        }
        break;
      }
      case 'message:history': {
        const p = payload as { messages?: Message[] };
        const msgs = p?.messages ?? [];
        const mode = this.historyMode.get(conversationId) ?? 'initial';
        if (mode === 'initial') chat.setMessages(conversationId, msgs);
        else chat.prependMessages(conversationId, msgs);
        this.historyMode.delete(conversationId);
        break;
      }
      case 'ai:typing': {
        const p = payload as { on?: boolean };
        chat.setAiTyping(conversationId, !!p?.on);
        break;
      }
      case 'ai:stream:start': {
        const p = payload as { stream_id?: string; source_message_id?: number };
        if (p?.stream_id) {
          chat.setAiTyping(conversationId, false);
          chat.startAiStream(conversationId, p.stream_id, p.source_message_id);
        }
        break;
      }
      case 'ai:stream:delta': {
        const p = payload as { stream_id?: string; seq?: number; delta?: string };
        if (p?.stream_id && typeof p.seq === 'number' && typeof p.delta === 'string') {
          chat.appendAiStreamDelta(conversationId, p.stream_id, p.seq, p.delta);
        }
        break;
      }
      case 'ai:stream:end': {
        const p = payload as { stream_id?: string; message?: Message };
        if (p?.message && typeof p.message.id === 'number') {
          chat.appendMessage(conversationId, p.message);
        }
        if (p?.stream_id) chat.endAiStream(conversationId, p.stream_id);
        break;
      }
      case 'ai:stream:error': {
        const p = payload as { stream_id?: string; retrying?: boolean; message?: string };
        if (p?.stream_id && p.retrying) {
          chat.markAiStreamRetrying(conversationId, p.stream_id);
        } else if (p?.stream_id) {
          chat.endAiStream(conversationId, p.stream_id);
          emitFeedback('warning', p.message || '知会回复失败，请稍后重试');
        }
        break;
      }
      case 'error': {
        const p = payload as { code?: number; message?: string };
        this.handleError(conversationId, p?.code ?? ErrorCode.SERVER_ERROR, p?.message ?? '实时错误');
        break;
      }
      // U1：成果新增/更新（payload: { artifact }）
      case 'artifact:upsert': {
        const p = payload as { artifact?: Artifact };
        if (p?.artifact && typeof p.artifact.id === 'number') {
          useArtifactStore.getState().upsertArtifact(conversationId, p.artifact);
        }
        break;
      }
      // U1：成果确认（payload: { id, state, confirmed_by, confirmed_at }）
      case 'artifact:confirm': {
        const p = payload as { id?: number; state?: string; confirmed_by?: number; confirmed_at?: string };
        if (typeof p?.id === 'number') {
          useArtifactStore.getState().patchArtifact(conversationId, p.id, {
            state: (p.state as 'confirmed' | 'pending' | 'draft') ?? 'confirmed',
            confirmed_by: p.confirmed_by ?? null,
            confirmed_at: p.confirmed_at ?? null,
          });
        }
        break;
      }
      // U1：群级 AI 模式切换（payload: { conversation_id, ai_mode }）
      case 'ai:mode': {
        const p = payload as { conversation_id?: number; ai_mode?: string };
        const mode = p?.ai_mode;
        if ((mode === 'quiet' || mode === 'partner' || mode === 'host')) {
          useArtifactStore.getState().setAiMode(conversationId, mode);
        }
        break;
      }
      // U1：AI 主动提示（payload: AiNotice）
      case 'ai:notice': {
        const p = payload as AiNotice;
        if (p && typeof p.conversation_id === 'number') {
          useArtifactStore.getState().addNotice(conversationId, p);
        }
        break;
      }
      default:
        break;
    }
  }

  private handleError(conversationId: number, code: number, message: string) {
    const chat = useChatStore.getState();
    chat.setAiTyping(conversationId, false);
    if (code === ErrorCode.AUTH_INVALID_TOKEN || code === ErrorCode.AUTH_EXPIRED) {
      useAuthStore.getState().logout();
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('zhihui:auth-expired'));
      }
      return;
    }
    if (code === ErrorCode.AI_KEY_MISSING) {
      emitFeedback('warning', 'AI 未配置，请联系创始人');
      return;
    }
    if (code === ErrorCode.AI_TIMEOUT) {
      emitFeedback('warning', '知会响应超时，请稍后重试');
      return;
    }
    if (code === ErrorCode.AI_MODEL_UNAVAILABLE) {
      emitFeedback('warning', message || '当前 AI 模型不可用');
      return;
    }
    if (code === ErrorCode.FILE_NOT_FOUND) {
      emitFeedback('error', '文件不存在或已清理');
      return;
    }
    emitFeedback('error', message || '发生错误');
  }

  /** 连接（并加入）某会话房间；已连接则复用 */
  connect(conversationId: number): PartySocket {
    const existing = this.sockets.get(conversationId);
    if (existing && this.getStatus(conversationId) !== 'closed') return existing;
    if (existing) {
      existing.close();
      this.sockets.delete(conversationId);
    }
    this.statuses.set(conversationId, 'connecting');
    const socket = this.makeSocket(conversationId);
    this.sockets.set(conversationId, socket);
    return socket;
  }

  private sendHistoryRequest(
    socket: PartySocket,
    conversationId: number,
    mode: 'initial' | 'older',
    cursor: number | undefined,
    limit: number,
  ): void {
    this.historyMode.set(conversationId, mode);
    socket.send(
      JSON.stringify({
        type: 'message:history',
        payload: { conversationId, cursor, limit },
      }),
    );
  }

  private flushPendingHistory(conversationId: number): void {
    const pending = this.pendingHistory.get(conversationId);
    const socket = this.sockets.get(conversationId);
    if (!pending || !socket || this.getStatus(conversationId) !== 'open') return;
    this.pendingHistory.delete(conversationId);
    this.sendHistoryRequest(socket, conversationId, pending.mode, pending.cursor, pending.limit);
  }

  /** 请求历史（initial 替换 / older 前插更旧消息） */
  fetchHistory(conversationId: number, mode: 'initial' | 'older' = 'initial', cursor?: number, limit = 50): void {
    const socket = this.sockets.get(conversationId) ?? this.connect(conversationId);
    if (this.getStatus(conversationId) !== 'open') {
      this.pendingHistory.set(conversationId, { mode, cursor, limit });
      return;
    }
    this.sendHistoryRequest(socket, conversationId, mode, cursor, limit);
  }

  /** 当前连接状态（connecting/open/closed/undefined） */
  getStatus(conversationId: number): 'connecting' | 'open' | 'closed' | undefined {
    return this.statuses.get(conversationId);
  }

  private sendNow(
    socket: PartySocket,
    conversationId: number,
    content: string,
    attachments?: AttachmentInput[],
    replyTo?: number | null,
  ): void {
    const hasImage = Array.isArray(attachments)
      ? attachments.some((a) => /\.(png|jpe?g|gif|webp)$/i.test(a.ext ?? ''))
      : false;
    socket.send(
      JSON.stringify({
        type: 'message:send',
        payload: { conversationId, content, attachments: attachments ?? [], hasImage, replyTo: replyTo ?? null },
      }),
    );
  }

  private clearPendingMessages(conversationId: number, notify = false): void {
    const pending = this.pendingMessages.get(conversationId) ?? [];
    for (const item of pending) clearTimeout(item.timer);
    this.pendingMessages.delete(conversationId);
    if (notify && pending.length > 0) emitFeedback('error', '连接未建立，暂存消息未发送');
  }

  private flushPendingMessages(conversationId: number): void {
    const socket = this.sockets.get(conversationId);
    if (!socket || this.getStatus(conversationId) !== 'open') return;
    const pending = this.pendingMessages.get(conversationId) ?? [];
    if (pending.length === 0) return;
    this.pendingMessages.delete(conversationId);
    for (const item of pending) {
      clearTimeout(item.timer);
      this.sendNow(socket, conversationId, item.content, item.attachments, item.replyTo);
    }
  }

  private queueMessage(
    conversationId: number,
    content: string,
    attachments?: AttachmentInput[],
    replyTo?: number | null,
  ): void {
    const timer = setTimeout(() => {
      const list = this.pendingMessages.get(conversationId) ?? [];
      const next = list.filter((item) => item.timer !== timer);
      if (next.length) this.pendingMessages.set(conversationId, next);
      else this.pendingMessages.delete(conversationId);
      emitFeedback('error', '连接未建立，消息未发送，请稍后重试');
    }, 12_000);
    const list = this.pendingMessages.get(conversationId) ?? [];
    list.push({ content, attachments, replyTo, timer });
    this.pendingMessages.set(conversationId, list);
    emitFeedback('info', '连接建立后将自动发送');
  }

  /** 发送消息（仅 PartyKit 单一路径），返回是否成功发送或暂存待发 */
  sendMessage(
    conversationId: number,
    content: string,
    attachments?: AttachmentInput[],
    replyTo?: number | null,
  ): boolean {
    let socket = this.sockets.get(conversationId);
    const status = this.getStatus(conversationId);
    if (!socket || status === 'closed') {
      socket = this.connect(conversationId);
    }
    if (this.getStatus(conversationId) !== 'open') {
      this.queueMessage(conversationId, content, attachments, replyTo);
      return true;
    }
    this.sendNow(socket, conversationId, content, attachments, replyTo);
    return true;
  }

  /** 断开单房间 */
  disconnect(conversationId: number): void {
    const socket = this.sockets.get(conversationId);
    if (socket) {
      socket.close();
      this.sockets.delete(conversationId);
      this.pendingHistory.delete(conversationId);
      this.clearPendingMessages(conversationId);
    }
  }

  /** 断开全部（登出时） */
  disconnectAll(): void {
    this.sockets.forEach((s) => s.close());
    this.sockets.clear();
    this.historyMode.clear();
    for (const conversationId of this.pendingMessages.keys()) this.clearPendingMessages(conversationId);
    this.pendingHistory.clear();
    this.statuses.clear();
    this.degradedNotified.clear();
  }
}

/** 全局单例 */
export const socketManager = new SocketManager();
